import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, BookOpen } from 'lucide-react';
import axios from 'axios';
import SocialLinks from '../components/SocialLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/blog/posts`)
      .then(({ data }) => setPosts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-page">
      <Helmet>
        <title>Blog | My Direct Debits — Tips on Managing Payments & Subscriptions</title>
        <meta name="description" content="Expert advice on managing direct debits, subscriptions, and recurring payments. Learn how to stay in control of your money." />
        <link rel="canonical" href="https://mddreminder.uk/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog | My Direct Debits — Payment Tips & Guides" />
        <meta property="og:description" content="Expert advice on managing direct debits, subscriptions, and recurring payments. Learn how to stay in control of your money." />
        <meta property="og:url" content="https://mddreminder.uk/blog" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Blog | My Direct Debits — Payment Tips & Guides" />
        <meta name="twitter:description" content="Expert advice on managing direct debits, subscriptions, and recurring payments." />
      </Helmet>

      <header className="glass-header px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-8 w-8" />
            <span className="font-heading font-semibold text-dark">MDD</span>
          </Link>
          <span className="text-text-light">|</span>
          <span className="font-heading font-semibold text-dark">Blog</span>
          <div className="ml-auto">
            <Link to="/auth" className="text-primary font-semibold text-sm hover:underline">Sign In</Link>
          </div>
        </div>
      </header>

      <main className="px-4 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-heading font-bold text-dark text-3xl sm:text-4xl mb-3">
              Payment Tips & Guides
            </h1>
            <p className="text-text-secondary text-base max-w-xl mx-auto">
              Expert advice on managing direct debits, subscriptions, and recurring payments. Stay informed, stay in control.
            </p>
          </div>

          <div className="mb-12" data-testid="blog-intro-video">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-lg border border-border bg-black" style={{ paddingTop: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/jMXqslCEQbA?rel=0"
                title="My Direct Debits — Stay Informed, Stay in Control"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-text-secondary py-20">No posts yet. Check back soon.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group card-default overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`blog-card-${post.slug}`}
                >
                  {post.images?.hero?.url ? (
                    <img
                      src={post.images.hero.url}
                      alt={post.images.hero.alt || post.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-48 bg-primary-light flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-primary/30" />
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="font-heading font-bold text-dark text-lg mb-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-16 mb-8">
            <Link
              to="/auth"
              className="btn-primary inline-block px-8 py-4 text-base font-semibold"
              data-testid="blog-cta-btn"
            >
              Start Tracking Your Payments Free
            </Link>
            <p className="text-text-light text-sm mt-3">No card required. No bank access needed.</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-border px-4 py-8 text-center">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-6 text-sm text-text-secondary">
          <Link to="/" className="hover:text-primary">Home</Link>
          <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary">Terms</Link>
          <Link to="/cookies" className="hover:text-primary">Cookies</Link>
        </div>
        <div className="mt-4">
          <SocialLinks />
        </div>
        <p className="text-text-light text-xs mt-4">&copy; 2026 Voliciti Group Ventures Ltd. All rights reserved.</p>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "My Direct Debits Blog",
        "description": "Expert advice on managing direct debits, subscriptions, and recurring payments.",
        "url": "https://mddreminder.uk/blog",
        "publisher": {
          "@type": "Organization",
          "name": "Voliciti Group Ventures Ltd",
          "url": "https://mddreminder.uk"
        }
      })}} />
    </div>
  );
}
